import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  CalendarDaysIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon,
  UserIcon,
  AcademicCapIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  BellIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { api } from '../../utils/api';
import UserAvatar from '../../components/UserAvatar';
import { DateHelper } from '../../utils/dateHelper';

interface Booking {
  id: string;
  date: Date;
  startTime: string;
  endTime: string;
  duration: number;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  totalPrice: number;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  student: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string;
  };
  teacher: {
    id: string;
    title: string;
    user: {
      firstName: string;
      lastName: string;
    };
  };
  course: {
    id: string;
    title: string;
    subject: string;
  };
}

interface BookingStats {
  total: number;
  pending: number;
  confirmed: number;
  completed: number;
  cancelled: number;
  totalRevenue: number;
  thisMonthRevenue: number;
  newBookings: number;
  todayBookings: number;
  urgentBookings: number;
}

const AdminBookings = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [stats, setStats] = useState<BookingStats>({
    total: 0,
    pending: 0,
    confirmed: 0,
    completed: 0,
    cancelled: 0,
    totalRevenue: 0,
    thisMonthRevenue: 0,
    newBookings: 0,
    todayBookings: 0,
    urgentBookings: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled'>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [showFilters, setShowFilters] = useState(false);

  // 新增：可视性增强状态
  const [viewedBookings, setViewedBookings] = useState<Set<string>>(new Set());
  const [newBookingNotifications, setNewBookingNotifications] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  // 判断预约是否为新的（4小时内创建）
  const isNewBooking = (booking: Booking) => {
    if (viewedBookings.has(booking.id)) return false;
    
    const now = new Date().getTime();
    const createdAt = new Date(booking.createdAt).getTime();
    const hoursDiff = (now - createdAt) / (1000 * 3600);
    
    return hoursDiff <= 4;
  };

  // 判断预约是否紧急（待确认且时间在48小时内）
  const isUrgentBooking = (booking: Booking) => {
    if (booking.status !== 'pending') return false;
    
    const now = new Date().getTime();
    const bookingTime = new Date(booking.date).getTime();
    const hoursDiff = (bookingTime - now) / (1000 * 3600);
    
    return hoursDiff <= 48 && hoursDiff > 0;
  };

  // 自动标记预约为已查看
  const markAsViewed = useCallback((bookingId: string) => {
    setViewedBookings(prev => new Set(prev).add(bookingId));
    setNewBookingNotifications(prev => {
      const newSet = new Set(prev);
      newSet.delete(bookingId);
      return newSet;
    });
  }, []);

  // 预约卡片引用回调
  const bookingCardRef = useCallback((node: HTMLDivElement | null, bookingId: string) => {
    if (node && observerRef.current) {
      node.setAttribute('data-booking-id', bookingId);
      observerRef.current.observe(node);
    }
  }, []);

  // 设置Intersection Observer
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const bookingId = entry.target.getAttribute('data-booking-id');
          if (!bookingId) return;

          if (entry.isIntersecting) {
            // 预约卡片进入视口，2秒后标记为已查看
            setTimeout(() => {
              const booking = bookings.find(b => b.id === bookingId);
              if (booking && isNewBooking(booking)) {
                markAsViewed(bookingId);
              }
            }, 2000);
          }
        });
      },
      {
        threshold: 0.7,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [bookings, markAsViewed]);

  // 初始化新预约通知
  useEffect(() => {
    const newBookings = bookings
      .filter(booking => isNewBooking(booking))
      .map(booking => booking.id);
    
    setNewBookingNotifications(new Set(newBookings));
  }, [bookings]);

  // 获取预约列表和统计数据
  const fetchBookings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/bookings/admin', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const bookingsData = await response.json();
        setBookings(bookingsData);
        setFilteredBookings(bookingsData);
        calculateStats(bookingsData);
      } else {
        console.error('获取预约失败:', response.status, response.statusText);
        toast.error('获取预约列表失败');
        // 确保在错误时设置为空数组
        setBookings([]);
        setFilteredBookings([]);
        calculateStats([]);
      }
    } catch (error) {
      console.error('获取预约失败:', error);
      toast.error('获取预约列表失败');
      // 确保在错误时设置为空数组
      setBookings([]);
      setFilteredBookings([]);
      calculateStats([]);
    } finally {
      setLoading(false);
    }
  };

  // 计算统计数据
  const calculateStats = (bookingsData: Booking[]) => {
    // 确保bookingsData是数组
    const safeBookingsData = Array.isArray(bookingsData) ? bookingsData : [];
    
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const newStats = {
      total: safeBookingsData.length,
      pending: safeBookingsData.filter(b => b.status === 'pending').length,
      confirmed: safeBookingsData.filter(b => b.status === 'confirmed').length,
      completed: safeBookingsData.filter(b => b.status === 'completed').length,
      cancelled: safeBookingsData.filter(b => b.status === 'cancelled').length,
      totalRevenue: safeBookingsData
        .filter(b => b.status === 'completed')
        .reduce((sum, b) => sum + b.totalPrice, 0),
      thisMonthRevenue: safeBookingsData
        .filter(b => b.status === 'completed' && new Date(b.date) >= thisMonth)
        .reduce((sum, b) => sum + b.totalPrice, 0),
      newBookings: safeBookingsData.filter(b => isNewBooking(b)).length,
      todayBookings: safeBookingsData.filter(b => {
        const bookingDate = new Date(b.date);
        return bookingDate.toDateString() === today.toDateString();
      }).length,
      urgentBookings: safeBookingsData.filter(b => isUrgentBooking(b)).length
    };

    setStats(newStats);
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  // 筛选逻辑
  useEffect(() => {
    // 确保bookings是数组
    const safeBookings = Array.isArray(bookings) ? bookings : [];
    let filtered = [...safeBookings];

    // 搜索筛选
    if (searchTerm) {
      filtered = filtered.filter(booking => 
        booking.student?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.student?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.teacher?.user?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.teacher?.user?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.course?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.course?.subject?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // 状态筛选
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // 日期筛选
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(booking => {
        const bookingDate = new Date(booking.date);
        const bookingDay = new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate());
        
        switch (dateFilter) {
          case 'today':
            return bookingDay.getTime() === today.getTime();
          case 'week':
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() - today.getDay());
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            return bookingDay >= weekStart && bookingDay <= weekEnd;
          case 'month':
            return bookingDay.getMonth() === today.getMonth() && 
                   bookingDay.getFullYear() === today.getFullYear();
          default:
            return true;
        }
      });
    }

    setFilteredBookings(filtered);
  }, [bookings, searchTerm, statusFilter, dateFilter]);

  // 获取状态样式
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // 获取状态文本
  const getStatusText = (status: string) => {
    const statusMap = {
      pending: '待确认',
      confirmed: '已确认',
      completed: '已完成',
      cancelled: '已取消'
    };
    return statusMap[status as keyof typeof statusMap] || '未知';
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              预约管理
              {stats.newBookings > 0 && (
                <span className="ml-3 bg-red-500 text-white text-sm px-2 py-1 rounded-full animate-pulse">
                  {stats.newBookings} 个新预约
                </span>
              )}
            </h1>
            <p className="mt-2 text-gray-600">
              管理平台所有预约记录和统计数据，今日有 {stats.todayBookings} 个预约
            </p>
          </div>
          
          {/* 新预约和紧急提醒卡片 */}
          {(stats.newBookings > 0 || stats.urgentBookings > 0) && (
            <div className="mt-4 lg:mt-0 space-y-4 max-w-md">
              {/* 新预约提醒 */}
              {stats.newBookings > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <BellIcon className="h-6 w-6 text-blue-500 animate-bounce" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">有新的预约</h3>
                      <div className="mt-1 text-sm text-blue-600">
                        <p>📋 {stats.newBookings} 个新预约需要关注</p>
                        <p className="text-xs mt-1 opacity-75">滚动查看详情，系统将自动标记已查看</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* 紧急预约提醒 */}
              {stats.urgentBookings > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-orange-500 animate-pulse" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-orange-800">紧急预约提醒</h3>
                      <div className="mt-1 text-sm text-orange-600">
                        <p>⏰ {stats.urgentBookings} 个预约在48小时内需要确认</p>
                        <p className="text-xs mt-1 opacity-75">请及时通知相关教师处理</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 增强统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {/* 总预约数 */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center">
            <CalendarDaysIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">总预约</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        {/* 待确认预约 */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg shadow-md p-4 relative">
          <div className="flex items-center">
            <ClockIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-900">待确认</p>
              <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
            </div>
          </div>
          {/* 紧急预约红点 */}
          {stats.urgentBookings > 0 && (
            <div className="absolute -top-1 -right-1 flex items-center">
              <div className="w-5 h-5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                {stats.urgentBookings}
              </div>
            </div>
          )}
        </div>

        {/* 已确认预约 */}
        <div className="bg-green-50 border border-green-200 rounded-lg shadow-md p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-900">已确认</p>
              <p className="text-2xl font-bold text-green-700">{stats.confirmed}</p>
            </div>
          </div>
        </div>

        {/* 已完成预约 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg shadow-md p-4">
          <div className="flex items-center">
            <CheckCircleSolid className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-900">已完成</p>
              <p className="text-2xl font-bold text-blue-700">{stats.completed}</p>
            </div>
          </div>
        </div>

        {/* 已取消预约 */}
        <div className="bg-red-50 border border-red-200 rounded-lg shadow-md p-4">
          <div className="flex items-center">
            <XCircleIcon className="h-8 w-8 text-red-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-900">已取消</p>
              <p className="text-2xl font-bold text-red-700">{stats.cancelled}</p>
            </div>
          </div>
        </div>

        {/* 本月收入 */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg shadow-md p-4">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-purple-900">本月收入</p>
              <p className="text-lg font-bold text-purple-700">¥{stats.thisMonthRevenue.toFixed(0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 次要统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {/* 新预约数 */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 relative">
          <div className="flex items-center">
            <BellIcon className="h-5 w-5 text-indigo-600" />
            <div className="ml-2">
              <p className="text-xs text-indigo-900">新预约</p>
              <p className="text-lg font-bold text-indigo-700">{stats.newBookings}</p>
            </div>
          </div>
          {stats.newBookings > 0 && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
          )}
        </div>

        {/* 今日预约 */}
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
          <div className="flex items-center">
            <CalendarDaysIcon className="h-5 w-5 text-teal-600" />
            <div className="ml-2">
              <p className="text-xs text-teal-900">今日预约</p>
              <p className="text-lg font-bold text-teal-700">{stats.todayBookings}</p>
            </div>
          </div>
        </div>

        {/* 紧急预约数 */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 relative">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-orange-600" />
            <div className="ml-2">
              <p className="text-xs text-orange-900">紧急预约</p>
              <p className="text-lg font-bold text-orange-700">{stats.urgentBookings}</p>
            </div>
          </div>
          {stats.urgentBookings > 0 && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
          )}
        </div>

        {/* 总收入 */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="flex items-center">
            <ChartBarIcon className="h-5 w-5 text-emerald-600" />
            <div className="ml-2">
              <p className="text-xs text-emerald-900">总收入</p>
              <p className="text-lg font-bold text-emerald-700">¥{stats.totalRevenue.toFixed(0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          {/* 搜索框 */}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜索学生、教师或课程..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* 筛选按钮 */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <FunnelIcon className="h-5 w-5 mr-2" />
            筛选
          </button>
        </div>

        {/* 展开的筛选选项 */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* 状态筛选 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">状态</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">全部状态</option>
                  <option value="pending">待确认</option>
                  <option value="confirmed">已确认</option>
                  <option value="completed">已完成</option>
                  <option value="cancelled">已取消</option>
                </select>
              </div>

              {/* 日期筛选 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">日期</label>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">全部日期</option>
                  <option value="today">今天</option>
                  <option value="week">本周</option>
                  <option value="month">本月</option>
                </select>
              </div>

              {/* 清除筛选 */}
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setDateFilter('all');
                    setShowFilters(false);
                  }}
                  className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  清除筛选
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 预约列表 */}
      {(!Array.isArray(filteredBookings) || filteredBookings.length === 0) ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">暂无预约记录</h3>
          <p className="text-gray-500">
            {searchTerm || statusFilter !== 'all' || dateFilter !== 'all' 
              ? '没有符合筛选条件的预约' 
              : '系统中还没有预约记录'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="divide-y divide-gray-200">
            {filteredBookings.map((booking) => {
              const isNew = isNewBooking(booking);
              const isUrgent = isUrgentBooking(booking);
              
              return (
                <div 
                  key={booking.id} 
                  ref={(node) => bookingCardRef(node, booking.id)}
                  className={`p-6 hover:bg-gray-50 transition-colors relative ${
                    isNew ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  } ${
                    isUrgent ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                  }`}
                >
                  {/* 新预约和紧急预约标识 */}
                  <div className="absolute top-4 right-4 flex items-center space-x-2">
                    {isNew && (
                      <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">
                        新预约
                      </span>
                    )}
                    {isUrgent && (
                      <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">
                        紧急
                      </span>
                    )}
                    {(isNew || isUrgent) && (
                      <div className="animate-pulse">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0 relative">
                            <UserAvatar
                              avatar={booking.student?.avatar}
                              firstName={booking.student?.firstName}
                              lastName={booking.student?.lastName}
                              size="md"
                            />
                            {/* 新预约或紧急预约红点 */}
                            {(isNew || isUrgent) && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping"></div>
                            )}
                          </div>
                          <div>
                            <p className="text-lg font-medium text-gray-900 flex items-center">
                              {booking.student.firstName} {booking.student.lastName}
                              {isNew && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                  新学生
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-gray-500">{booking.student.email}</p>
                          </div>
                        </div>
                        <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getStatusStyle(booking.status)}`}>
                          {getStatusText(booking.status)}
                        </span>
                      </div>

                      {/* 新预约额外信息 */}
                      {isNew && (
                        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center">
                            <BellIcon className="h-4 w-4 text-blue-600 mr-2" />
                            <p className="text-sm text-blue-800">
                              <span className="font-medium">新预约</span> - 
                              {(() => {
                                const now = new Date().getTime();
                                const createdAt = new Date(booking.createdAt).getTime();
                                const hoursDiff = Math.floor((now - createdAt) / (1000 * 3600));
                                const minutesDiff = Math.floor((now - createdAt) / (1000 * 60));
                                
                                if (hoursDiff >= 1) {
                                  return ` ${hoursDiff}小时前提交`;
                                } else {
                                  return ` ${minutesDiff}分钟前提交`;
                                }
                              })()}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* 紧急预约额外信息 */}
                      {isUrgent && (
                        <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                          <div className="flex items-center">
                            <ExclamationTriangleIcon className="h-4 w-4 text-orange-600 mr-2" />
                            <p className="text-sm text-orange-800">
                              <span className="font-medium">紧急预约</span> - 
                              {(() => {
                                const now = new Date().getTime();
                                const bookingTime = new Date(booking.date).getTime();
                                const hoursDiff = Math.floor((bookingTime - now) / (1000 * 3600));
                                
                                if (hoursDiff <= 24) {
                                  return ` 距离预约时间不足24小时！`;
                                } else {
                                  return ` 距离预约时间${hoursDiff}小时`;
                                }
                              })()}
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="flex items-center">
                          <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {booking.teacher.title} {booking.teacher.user.firstName} {booking.teacher.user.lastName}
                            </p>
                            <p className="text-xs text-gray-500">教师</p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <AcademicCapIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{booking.course.title}</p>
                            <p className="text-xs text-gray-500">{booking.course.subject}</p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <CalendarDaysIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            {(() => {
                              const dateStr = typeof booking.date === 'string' 
                                ? booking.date 
                                : DateHelper.formatDateForInput(new Date(booking.date))
                              const { localDateObj, localStartTime, localEndTime, timezoneNote } = DateHelper.formatBookingTime(
                                dateStr, 
                                booking.startTime, 
                                booking.endTime
                              )
                              return (
                                <>
                                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                                    {localDateObj.toLocaleDateString('zh-CN')}
                                    {timezoneNote && (
                                      <GlobeAltIcon className="h-3 w-3 text-indigo-500" title="已转换为本地时区" />
                                    )}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {localStartTime} - {localEndTime || booking.endTime}
                                  </p>
                                </>
                              )
                            })()}
                          </div>
                        </div>

                        <div className="flex items-center">
                          <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">¥{booking.totalPrice}</p>
                            <p className="text-xs text-gray-500">{booking.duration}小时</p>
                          </div>
                        </div>
                      </div>

                      {booking.notes && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium">备注：</span>{booking.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminBookings
